import styled from "@emotion/styled";
import { QueryFunctionContext, useInfiniteQuery } from "@tanstack/react-query";
import { getFavorites } from "api/clients/favorites/favorites.request";
import useGetPersistParams from "custom-hooks/useGetPersintParamas.hook";
import useWindowSize from "custom-hooks/useWindowsSize.hook";
import { useTranslations } from "next-intl";
import Link from "next/link";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import LoadingBubbles from "@components/loading-bubbles";
import { MessageResults } from "@components/messages-result";
import PropertyCard from "@components/property-card";
import { CPagesPath } from "shared/models/const/pages-path";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import { getLastedPropertySee } from "api/clients/lasted-properties/lasted-property-see.request";

export default function LastedSeeProperty() {
  const t = useTranslations("ProfileHistorySearchPage");
  const ref = useGetPersistParams();
  const size = useWindowSize();
  const width: any = size?.width;

  const request$ = ({ pageParam = 1 }: QueryFunctionContext) => {
    return getLastedPropertySee({ page: pageParam });
  };

  const {
    data: infiniteFavorites,
    fetchNextPage,
    isLoading,
    hasNextPage,
    isError,
  } = useInfiniteQuery(["favorites", "infinite"], request$, {
    getNextPageParam: ({ meta }, pages: any) => {
      if (meta.last_page == meta.current_page) {
        return false;
      }
      return pages.length + 1;
    },
    refetchOnWindowFocus: false,
    // Forcing react-query to always reset data with fresh data every time
    cacheTime: 0,
  });

  const getMore = () => {
    if (!isLoading && hasNextPage) {
      fetchNextPage();
    }
  };

  if (isLoading && infiniteFavorites === undefined) {
    return (
      <SearchResults>
        <LoadingBubbles message={t("loading")} />
      </SearchResults>
    );
  }

  if (isError) {
    return (
      <SearchResults>
        <MessageResults message={t("error")} />
      </SearchResults>
    );
  }

  if (infiniteFavorites && infiniteFavorites.pages[0].data.length === 0) {
    return (
      <SearchResults>
        <NoFavorites>
          <span>
            {t("no-favorites")}
            <br />
            <HeartBrokenIcon />
          </span>
        </NoFavorites>
      </SearchResults>
    );
  }

  return (
    <InfiniteScroll
      loadMore={getMore}
      hasMore={hasNextPage}
      loader={<LoadingBubbles message={t("loading")} />}
    >
      <SearchContentResults>
        {infiniteFavorites &&
          infiniteFavorites.pages?.map(({ data }) =>
            data.map((property, i) => (
              <Link
                key={i}
                href={CPagesPath.es.property_detail(property) + ref}
                passHref
              >
                <CardWrapper key={i} target={width > 991 ? "_blank" : ""}>
                  <StyledPropertyCard {...property} />
                </CardWrapper>
              </Link>
            ))
          )}
      </SearchContentResults>
    </InfiniteScroll>
  );
}

const StyledPropertyCard = styled(PropertyCard)`
  @media screen and (max-width: 500px) {
    width: 315px;
  }
`;

const CardWrapper = styled.a`
  display: flex;
  justify-content: center;
  margin: 5px;
`;

const SearchContentResults = styled.div`
  display: grid;
  justify-content: space-between;
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  gap: 12px 8px;
  margin-top: 30px;
`;

const SearchResults = styled.div`
  display: flex;
  margin-top: 30px;
  justify-content: center;
  align-items: center;
`;

const NoFavorites = styled.div`
  display: grid;
  place-content: center;
  margin-top: 30px;
  text-align: center;

  span {
    color: var(--color-brand-blue-main);
    font-weight: bold;
  }
`;
